<script setup lang="ts">
import { whenever } from '@vueuse/core';
import { computed, reactive } from 'vue';
import { useStore } from 'vuex';

import AddToCartQuantitySelect from '@/components/base/add-to-cart/AddToCartQuantitySelect.vue';
import Modal from '@/components/base/layout/Modal.vue';
import Header4 from '@/components/base/typography/Header4.vue';
import OutOfStock from '@/components/pdp/OutOfStock.vue';
import VariantSizeSelector from '@/components/ppa/VariantSizeSelector.vue';
import { useCallback } from '@/composables/useCallback';
import { useCart } from '@/composables/useCart';
import { getMoneyValue } from '@/lib/product/prices';
import { ListMetadata } from '@/utils/analytics';
import { ListSourceAttribution } from '@/utils/analytics/listSourceAttribution';
import { sendModalViewed } from '@/utils/analytics/modalViewed';
import {
  formatNutsAddToCartItemFromProductProjection,
  sendProductAddedEvent,
} from '@/utils/analytics/productAddedEvent';
import { getCartId } from '@/utils/cart';
import { Money } from '@/utils/money';
import { PurchaseOptions } from '@/utils/product';
import { NutsProductProjection } from '@/utils/productProjection';

const {
  indexName,
  isOpen,
  itemListName,
  listMetadata,
  listSourceAttribution,
  pageSection,
  position,
  product,
} = defineProps<{
  indexName?: string;
  isOpen: boolean;
  itemListName?: string;
  listMetadata?: ListMetadata;
  listSourceAttribution?: ListSourceAttribution;
  pageSection?: string;
  position: number;
  product: NutsProductProjection;
}>();

const emit = defineEmits<{
  'handle-close': [];
}>();

const selections: PurchaseOptions = reactive({
  markedAsGift: false,
  quantity: 1,
  sku: product.variants[0]?.sku,
  variation: product.variants[0]?.id ?? 1,
});

const selectedVariant = computed(() => product.variants.find((v) => v.sku === selections.sku));

const totalCost = computed(() => {
  const {
    variant: { listPrice },
  } = NutsProductProjection.toDisplayVariant(product, { sku: selections.sku });
  return Money.multiply(getMoneyValue(listPrice, selections.quantity), selections.quantity);
});

const handleClose = () => {
  emit('handle-close');
  selections.sku = product.variants[0]?.sku;
  selections.variation = product.variants[0]?.id ?? 1;
};

const store = useStore();
const { addToCart, cart } = useCart(store);

const handleAddToCart = useCallback(async () => {
  sendProductAddedEvent(
    formatNutsAddToCartItemFromProductProjection(product, selections.sku, {
      cartId: getCartId(cart.value),
      indexName,
      itemListName,
      listSourceAttribution,
      markedAsGift: false,
      pageSection: 'Size Selector Modal',
      position,
      quantity: selections.quantity,
    }),
  );

  await addToCart(
    {
      cart_sku: {
        list_metadata: JSON.stringify(listMetadata),
        quantity: selections.quantity,
        sku_external_id: selections.sku,
      },
    },
    {
      postAddToCartCallback: () => null,
      skipCartAdded: false,
    },
  );

  handleClose();
});

whenever(
  () => isOpen,
  () => {
    sendModalViewed({
      modalName: 'Size Selector Modal',
      productId: product.productKey,
      source: pageSection,
    });
  },
  { immediate: true },
);
</script>

<template>
  <Modal
    aria-label="Select size"
    :hasControls="false"
    :isOpen
    :legacyHeaderPadding="false"
    skipFirstElementOnFocus
    width="md:max-w-xl"
    @handle-close="handleClose"
  >
    <template #header>
      <Header4>{{ product.name }}</Header4>
    </template>
    <template #body>
      <VariantSizeSelector
        v-model="selections"
        class="my-4"
        :isGiftCertificate="false"
        :masterVariantId="product.variants[0].masterVariantId"
        showOutOfStock
        :variants="product.variants.filter((v) => v.customAvailability?.includes('PDP'))"
      />
      <Transition name="fade" mode="out-in">
        <OutOfStock
          v-if="selectedVariant?.backordered"
          class="mb-8"
          :productKey="product.productKey"
        />
        <AddToCartQuantitySelect
          v-else
          v-model="selections.quantity"
          :isLoading="handleAddToCart.isPending"
          :totalCost
          @submit="handleAddToCart.execute"
        />
      </Transition>
    </template>
  </Modal>
</template>
